import { ComponentType, useEffect, useRef, useState } from "react";

import {
  BigNumberIcon,
  HorizontalBar,
  LineChartIcon,
  PieChartIcon,
  VerticalBarIcon
} from "@/assets/icons";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WidgetType } from "@/types/widget";

import { twMerge } from "tailwind-merge";

import WidgetConfigurationPreview from "./WidgetConfigurationPreview";

const DEFAULT_CHART_HEIGHT = 384;
const MIN_CHART_HEIGHT = 200;
const TABS_HEADER_MARGIN_BOTTOM = 12;

const EXAMPLE_DATA: { name: string; value: string }[] = [
  { name: "Jan", value: "120" },
  { name: "Feb", value: "98" },
  { name: "Mar", value: "150" },
  { name: "Apr", value: "200" },
  { name: "May", value: "175" },
  { name: "Jun", value: "220" },
  { name: "Jul", value: "300" },
  { name: "Aug", value: "280" },
  { name: "Sep", value: "260" },
  { name: "Oct", value: "310" }
];

interface ChartConfig {
  label: string;
  value: WidgetType;
  Icon: ComponentType<{ className?: string }>;
}

const LIST_CHARTS: ChartConfig[] = [
  {
    label: "Bar Vertical",
    value: WidgetType.BAR_V,
    Icon: VerticalBarIcon
  },
  {
    label: "Bar Horizontal",
    value: WidgetType.BAR_H,
    Icon: HorizontalBar
  },
  {
    label: "Donut",
    value: WidgetType.DONUT,
    Icon: PieChartIcon
  },
  {
    label: "Line",
    value: WidgetType.LINE,
    Icon: LineChartIcon
  },
  {
    label: "Big Number",
    value: WidgetType.BIG_NUMBER,
    Icon: BigNumberIcon
  }
];

interface WidgetConfigurationTabProps {
  widgetType: WidgetType;
  setWidgetType: (type: WidgetType) => void;
  // When true the chart-type tabs are disabled because the user is editing an
  // existing widget and changing its chart type is not supported in this flow.
  isEditing: boolean;
}

const WidgetConfigurationTab = ({
  widgetType,
  setWidgetType,
  isEditing
}: WidgetConfigurationTabProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tabsHeaderRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(DEFAULT_CHART_HEIGHT);

  useEffect(() => {
    const calculate = () => {
      if (!containerRef.current || !tabsHeaderRef.current) return;
      const totalHeight = containerRef.current.clientHeight;
      const headerHeight = tabsHeaderRef.current.clientHeight;
      setChartHeight(
        Math.max(
          MIN_CHART_HEIGHT,
          totalHeight - headerHeight - TABS_HEADER_MARGIN_BOTTOM
        )
      );
    };

    calculate();
    const ro = new ResizeObserver(calculate);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="flex-1 flex-shrink-0 px-4 h-full">
      <Tabs
        defaultValue={WidgetType.BAR_V}
        onValueChange={(v) => setWidgetType(v as WidgetType)}
        value={widgetType}
      >
        <div ref={tabsHeaderRef} className="flex justify-center mb-3">
          <TabsList>
            {LIST_CHARTS.map((chart) => (
              <TabsTrigger
                key={chart.value}
                value={chart.value}
                className={twMerge(
                  "flex justify-center items-center gap-1",
                  widgetType === chart.value
                    ? "bg-white rounded-lg disabled:bg-white disabled:opacity-100"
                    : ""
                )}
                disabled={isEditing}
              >
                <chart.Icon
                  className={twMerge(
                    "w-6 h-6",
                    widgetType === chart.value
                      ? "fill-teal-500"
                      : "fill-dim-gray"
                  )}
                />
                <span
                  className={twMerge(
                    "text-sm font-medium",
                    widgetType === chart.value
                      ? "text-teal-500"
                      : "text-dim-gray"
                  )}
                >
                  {chart.label}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent key={widgetType} value={widgetType}>
          <div
            data-testid="widget-configuration-preview"
            className="border border-whisper rounded-lg p-4 flex justify-center items-center overflow-hidden"
            style={{ height: chartHeight }}
          >
            <WidgetConfigurationPreview
              widgetType={widgetType}
              data={EXAMPLE_DATA}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WidgetConfigurationTab;
